// ProjectStateCard — the Settings tab's Status card: one dropdown over the
// project's operational `state` (active/inactive), every change confirmed, and
// written through PATCH /projects/update/:id/state.
//
// Named for the column it writes. `state` is NOT the lifecycle `status` the
// Danger Zone's archive/restore moves — two independent columns, two endpoints.
//
// Deliberately OUTSIDE the General draft: the pending state lives here, in its
// own useState, and saves through its own mutation, so the General Save/Reset
// footer neither saves nor resets it. The mutation invalidates {Projects, id},
// so the detail refetches and the dropdown, the card's head icon and the page
// header's chip all follow with no local copy of the project's state.
//
// Activating also writes the end date. The dialog prefills the project's
// CURRENT end date even when that date is already past — the "must be later
// than today" rule is enforced on confirm, not on open, so the field never
// opens pre-reddened.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Card, ConfirmModal, Field, Icon, Input, SelectDropdown } from '@/components/common';
import {
  useUpdateProjectStateMutation,
  type ProjectDetail,
  type ProjectState,
} from '@/api/endpoints/projectsApi';
import useModuleAccess from '@/hooks/useModuleAccess';
import { toDateInput, toIso, todayPlus } from '@/utils/date';
import { STATUS_META } from './projectColumns';

interface ProjectStateCardProps {
  project: ProjectDetail;
}

const STATES: ProjectState[] = ['active', 'inactive'];

const ProjectStateCard = ({ project }: ProjectStateCardProps) => {
  const { t } = useTranslation();
  const { canEdit } = useModuleAccess('projects');
  const [updateProjectState, { isLoading: moving }] = useUpdateProjectStateMutation();

  // An archived project's lifecycle has to be reversed before its operational
  // state means anything, so the control reads rather than edits until then.
  const archived = project.status === 'archived';

  // The state awaiting confirmation; null = no dialog open.
  const [pending, setPending] = useState<ProjectState | null>(null);
  const [endDate, setEndDate] = useState('');
  // The date rule only bites on confirm, so the error row stays hidden until
  // the user has actually tried once.
  const [attempted, setAttempted] = useState(false);

  const activating = pending === 'active';
  const minDate = todayPlus(1);
  // yyyy-mm-dd compares lexicographically — the module's date-ordering idiom.
  const dateInvalid = !endDate || endDate < minDate;

  // Every open re-prefills from the project's current end date, past or not.
  useEffect(() => {
    if (pending !== 'active') return;
    setEndDate(toDateInput(project.endDate));
    setAttempted(false);
  }, [pending, project.endDate]);

  const apply = async () => {
    if (!pending) return;
    // Invalid date — show the error, keep the dialog open, send nothing.
    if (activating && dateInvalid) {
      setAttempted(true);
      return;
    }
    try {
      await updateProjectState({
        id: project.id,
        body: activating ? { state: pending, endDate: toIso(endDate) } : { state: pending },
      }).unwrap();
      toast.success(
        t(activating ? 'projects_state_activated_toast' : 'projects_state_deactivated_toast'),
      );
      setPending(null);
    } catch {
      // Left open on failure so the user can retry; the toast says why.
      toast.error(t('error_generic'));
    }
  };

  return (
    <>
      <Card
        padded={false}
        head={
          <span className='project-detail__section-title'>
            <Icon name={STATUS_META[project.state].icon} size={15} />
            {t('projects_col_status')}
          </span>
        }
      >
        <div className='project-settings__form'>
          {/* The card head already says "Status", so a Field label would only
              repeat it — the trigger carries the name itself. */}
          <SelectDropdown
            size='lg'
            aria-label={t('projects_col_status')}
            value={pending ?? project.state}
            disabled={!canEdit || archived || moving}
            onChange={(next) => {
              if (next !== project.state) setPending(next);
            }}
            options={STATES.map((s) => ({
              value: s,
              label: t(`projects_status_${s}`),
              icon: STATUS_META[s].icon,
            }))}
          />
          {archived && (
            <p className='project-settings__hint'>{t('projects_state_archived_hint')}</p>
          )}
        </div>
      </Card>

      {/* ONE direction-aware confirm. No `confirmDisabled`: an invalid date is
          answered with an error row on confirm, not a dead button. */}
      <ConfirmModal
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={() => void apply()}
        tone={activating ? 'success' : 'warning'}
        icon={activating ? 'circle-check' : 'circle-pause'}
        title={t(activating ? 'projects_state_activate_title' : 'projects_state_deactivate_title')}
        description={t(
          activating ? 'projects_state_activate_body' : 'projects_state_deactivate_body',
        )}
        confirmLabel={t(
          activating ? 'projects_state_activate_title' : 'projects_state_deactivate_title',
        )}
        confirmVariant={activating ? 'primary' : 'danger'}
        confirmIcon={activating ? 'circle-check' : 'circle-pause'}
        loading={moving}
      >
        {activating && (
          <div className='project-state-confirm'>
            <Field
              label={t('projects_col_end')}
              labelTag='· DD-MM-YYYY'
              error={attempted && dateInvalid ? t('projects_state_date_invalid') : undefined}
            >
              <Input
                type='date'
                iconStart='calendar-check'
                error={attempted && dateInvalid}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Field>
          </div>
        )}
      </ConfirmModal>
    </>
  );
};

export default ProjectStateCard;
